import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, User, Heart, ShoppingCart, Menu, X, ChevronDown, Sparkles, TrendingUp, Grid3X3, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { categoriesAPI, brandsAPI } from '../services/api';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';

const SerenityHeader = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [cartBounce, setCartBounce] = useState(false);
  const [wishlistBounce, setWishlistBounce] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  
  const { itemCount: cartCount } = useCart();
  const { count: wishlistCount } = useWishlist();
  
  useEffect(() => {
    const handleCartUpdate = () => {
      setCartBounce(true);
      setTimeout(() => setCartBounce(false), 600);
    };
    
    const handleWishlistUpdate = () => {
      setWishlistBounce(true);
      setTimeout(() => setWishlistBounce(false), 600);
    };
    
    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('wishlistUpdated', handleWishlistUpdate);
    
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
    };
  }, []);
  
  useEffect(() => {
    if (cartCount > 0) {
      setCartBounce(true);
      const timer = setTimeout(() => setCartBounce(false), 600);
      return () => clearTimeout(timer);
    }
  }, [cartCount]);
  
  useEffect(() => {
    if (wishlistCount > 0) {
      setWishlistBounce(true);
      const timer = setTimeout(() => setWishlistBounce(false), 600);
      return () => clearTimeout(timer);
    }
  }, [wishlistCount]);

  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        const [catRes, brandRes] = await Promise.all([
          categoriesAPI.getAll(),
          brandsAPI.getAll()
        ]);
        if (isMounted) {
          setCategories(catRes.data || []);
          setBrands(brandRes.data || []);
        }
      } catch (error) {
        if (isMounted && error.name !== 'CanceledError') {
          console.error('Failed to fetch nav data');
        }
      }
    };
    
    fetchData();
    
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${searchQuery}`);
      setSearchQuery('');
    }
  };

  const toggleDropdown = (name) => {
    setActiveDropdown(activeDropdown === name ? null : name);
  };

  const collections = [
    { name: 'New Arrivals', slug: 'new-arrivals', description: 'Fresh luxury items', icon: Sparkles, color: 'text-[#D4AF37]' },
    { name: 'Best Sellers', slug: 'best-sellers', description: 'Top customer picks', icon: TrendingUp, color: 'text-[#D4AF37]' },
  ];

  return (
    <>
      {/* Top Banner - Luxury Gold */}
      <div className="bg-gradient-to-r from-[#D4AF37] via-[#F4E4BC] to-[#D4AF37] text-[#0A0A0A] text-center py-3 text-sm font-body font-semibold tracking-wide">
        Free Shipping on Orders $100+ • Luxury Collectibles • Premium Quality Guaranteed
      </div>

      {/* Main Header - Black */}
      <header className="bg-[#0A0A0A] border-b border-[#D4AF37]/20 shadow-lg shadow-[#D4AF37]/5 sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-4">
            {/* Logo - BOXVERRA */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37] to-[#B8960C] rounded-lg flex items-center justify-center transform group-hover:scale-105 transition-transform shadow-lg shadow-[#D4AF37]/30">
                <span className="text-[#0A0A0A] text-2xl font-bold font-heading">B</span>
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-white font-heading group-hover:text-[#D4AF37] transition-colors tracking-wide">
                  BOXVERRA
                </h1>
                <p className="text-xs text-[#D4AF37] font-body hidden sm:block tracking-widest uppercase">Luxury Collectibles</p>
              </div>
            </Link>

            {/* Search Bar - Desktop */}
            <div className="hidden lg:flex flex-1 max-w-xl mx-8">
              <form onSubmit={handleSearch} className="flex w-full">
                <Input
                  type="text"
                  placeholder="Search luxury items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="rounded-r-none bg-[#141414] border-2 border-[#D4AF37]/30 text-white placeholder-[#888888] focus:border-[#D4AF37] font-body text-base px-4 py-5"
                />
                <Button 
                  type="submit" 
                  className="rounded-l-none bg-[#D4AF37] hover:bg-[#B8960C] text-[#0A0A0A] px-6 font-semibold"
                >
                  <Search className="h-5 w-5" />
                </Button>
              </form>
            </div>

            {/* Right Icons */}
            <div className="flex items-center gap-2 sm:gap-4">
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setShowMobileSearch(!showMobileSearch)}
                className="lg:hidden text-white hover:bg-[#1A1A1A] hover:text-[#D4AF37] p-2"
              >
                <Search className="h-6 w-6" />
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="lg:hidden text-white order-first"
              >
                {isMenuOpen ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
              </Button>

              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/account')} 
                className="text-white hover:bg-[#1A1A1A] hover:text-[#D4AF37] p-2 transition-all duration-200 hover:scale-105"
              >
                <User className="h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8" />
              </Button>

              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/wishlist')} 
                className={`text-white hover:bg-[#1A1A1A] relative p-2 transition-all duration-300 ${wishlistBounce ? 'scale-125 animate-bounce' : 'hover:scale-105'}`}
              >
                <Heart className={`h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 transition-all duration-300 ${wishlistCount > 0 ? 'fill-[#D4AF37] text-[#D4AF37]' : ''}`} />
                {wishlistCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#D4AF37] text-[#0A0A0A] text-xs sm:text-sm rounded-full min-w-[22px] h-[22px] sm:min-w-[26px] sm:h-[26px] flex items-center justify-center font-bold px-1.5 shadow-lg">
                    {wishlistCount}
                  </span>
                )}
              </Button>

              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => navigate('/cart')} 
                className={`text-white hover:bg-[#1A1A1A] relative p-2 transition-all duration-300 ${cartBounce ? 'scale-125 animate-bounce' : 'hover:scale-105'}`}
              >
                <ShoppingCart className={`h-6 w-6 sm:h-7 sm:w-7 lg:h-8 lg:w-8 transition-all duration-300 ${cartCount > 0 ? 'text-[#D4AF37]' : ''}`} />
                {cartCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-[#D4AF37] text-[#0A0A0A] text-xs sm:text-sm rounded-full min-w-[22px] h-[22px] sm:min-w-[26px] sm:h-[26px] flex items-center justify-center font-bold px-1.5 shadow-lg">
                    {cartCount}
                  </span>
                )}
              </Button>
            </div>
          </div>

          {/* Search Bar - Mobile */}
          {showMobileSearch && (
            <div className="lg:hidden pb-3 animate-in slide-in-from-top duration-200">
              <form onSubmit={(e) => { handleSearch(e); setShowMobileSearch(false); }} className="flex w-full">
                <Input
                  type="text"
                  placeholder="Search luxury items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="rounded-r-none bg-[#141414] border-2 border-[#D4AF37]/30 text-white placeholder-[#888888] focus:border-[#D4AF37] font-body text-base px-4 py-3"
                />
                <Button 
                  type="submit" 
                  className="rounded-l-none bg-[#D4AF37] hover:bg-[#B8960C] text-[#0A0A0A] px-4"
                >
                  <Search className="h-5 w-5" />
                </Button>
              </form>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className={`border-t border-[#D4AF37]/20 bg-[#0A0A0A] ${isMenuOpen ? 'block' : 'hidden'} lg:block`} ref={dropdownRef}>
          <div className="container mx-auto px-4">
            <ul className="flex flex-col lg:flex-row lg:items-center lg:justify-center lg:gap-1 py-2">
              
              <li>
                <Link
                  to="/shop"
                  className="block py-3 px-4 text-base font-semibold text-white hover:text-[#D4AF37] hover:bg-[#1A1A1A] rounded-lg transition-all font-body"
                  onClick={() => { setIsMenuOpen(false); setActiveDropdown(null); }}
                >
                  Shop All
                </Link>
              </li>

              {/* Categories Dropdown */}
              <li className="relative">
                <button
                  onClick={() => toggleDropdown('categories')}
                  className={`flex items-center gap-1 py-3 px-4 text-base font-semibold transition-all font-body rounded-lg w-full lg:w-auto ${
                    activeDropdown === 'categories' ? 'text-[#D4AF37] bg-[#1A1A1A]' : 'text-white hover:text-[#D4AF37] hover:bg-[#1A1A1A]'
                  }`}
                >
                  Categories
                  <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === 'categories' ? 'rotate-180' : ''}`} />
                </button>
                
                {activeDropdown === 'categories' && (
                  <div className="lg:absolute lg:top-full lg:left-0 lg:mt-1 bg-[#141414] lg:shadow-2xl lg:shadow-[#D4AF37]/10 lg:rounded-2xl lg:border lg:border-[#D4AF37]/20 lg:min-w-[400px] lg:max-h-[480px] lg:overflow-y-auto z-50">
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <Grid3X3 className="h-4 w-4 text-[#D4AF37]" />
                        <p className="text-xs uppercase tracking-wider text-[#888888] font-body font-semibold">Shop by Category</p>
                      </div>
                      <div className="grid gap-1">
                        {categories.slice(0, 8).map((cat) => (
                          <Link
                            key={cat._id}
                            to={`/shop?category=${encodeURIComponent(cat.name)}`}
                            className="flex items-center gap-4 py-3 px-3 rounded-xl hover:bg-[#1A1A1A] transition-all group"
                            onClick={() => { setIsMenuOpen(false); setActiveDropdown(null); }}
                          >
                            {cat.image ? (
                              <div className="w-12 h-12 rounded-lg overflow-hidden bg-[#1A1A1A] flex-shrink-0 ring-2 ring-transparent group-hover:ring-[#D4AF37]/30 transition-all">
                                <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                              </div>
                            ) : (
                              <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                                <Grid3X3 className="h-5 w-5 text-[#D4AF37]" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <span className="text-white group-hover:text-[#D4AF37] font-body font-medium block truncate">{cat.name}</span>
                              <span className="text-xs text-[#888888] font-body">{cat.product_count || 0} products</span>
                            </div>
                            <ChevronDown className="h-4 w-4 text-[#888888] -rotate-90 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
                          </Link>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-[#D4AF37]/20">
                        <Link
                          to="/shop"
                          className="flex items-center justify-center gap-2 text-[#D4AF37] font-semibold text-sm font-body hover:underline py-2"
                          onClick={() => { setIsMenuOpen(false); setActiveDropdown(null); }}
                        >
                          View All Categories
                          <ChevronDown className="h-4 w-4 -rotate-90" />
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </li>

              {/* Brands Dropdown */}
              <li className="relative">
                <button
                  onClick={() => toggleDropdown('brands')}
                  className={`flex items-center gap-1 py-3 px-4 text-base font-semibold transition-all font-body rounded-lg w-full lg:w-auto ${
                    activeDropdown === 'brands' ? 'text-[#D4AF37] bg-[#1A1A1A]' : 'text-white hover:text-[#D4AF37] hover:bg-[#1A1A1A]'
                  }`}
                >
                  Brands
                  <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === 'brands' ? 'rotate-180' : ''}`} />
                </button>
                
                {activeDropdown === 'brands' && (
                  <div className="lg:absolute lg:top-full lg:left-0 lg:mt-1 bg-[#141414] lg:shadow-2xl lg:shadow-[#D4AF37]/10 lg:rounded-2xl lg:border lg:border-[#D4AF37]/20 lg:min-w-[360px] z-50">
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <Star className="h-4 w-4 text-[#D4AF37]" />
                        <p className="text-xs uppercase tracking-wider text-[#888888] font-body font-semibold">Shop by Brand</p>
                      </div>
                      <div className="grid gap-1">
                        {brands.slice(0, 8).map((brand) => (
                          <Link
                            key={brand._id}
                            to={`/shop?brand=${encodeURIComponent(brand.name)}`}
                            className="flex items-center gap-4 py-3 px-3 rounded-xl hover:bg-[#1A1A1A] transition-all group"
                            onClick={() => { setIsMenuOpen(false); setActiveDropdown(null); }}
                          >
                            {brand.logo ? (
                              <div className="w-10 h-10 rounded-lg overflow-hidden bg-white border border-[#D4AF37]/20 flex-shrink-0 p-1.5 group-hover:border-[#D4AF37]/50 transition-all">
                                <img src={brand.logo} alt={brand.name} className="w-full h-full object-contain" />
                              </div>
                            ) : (
                              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                                <span className="text-sm font-bold text-[#D4AF37]">{brand.name?.charAt(0)}</span>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <span className="text-white group-hover:text-[#D4AF37] font-body font-medium block truncate">{brand.name}</span>
                              <span className="text-xs text-[#888888] font-body">{brand.product_count || 0} products</span>
                            </div>
                          </Link>
                        ))}
                      </div>
                      <div className="mt-4 pt-4 border-t border-[#D4AF37]/20">
                        <Link
                          to="/brands"
                          className="flex items-center justify-center gap-2 text-[#D4AF37] font-semibold text-sm font-body hover:underline py-2"
                          onClick={() => { setIsMenuOpen(false); setActiveDropdown(null); }}
                        >
                          View All Brands
                          <ChevronDown className="h-4 w-4 -rotate-90" />
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </li>

              {/* Collections Dropdown */}
              <li className="relative">
                <button
                  onClick={() => toggleDropdown('collections')}
                  className={`flex items-center gap-1 py-3 px-4 text-base font-semibold transition-all font-body rounded-lg w-full lg:w-auto ${
                    activeDropdown === 'collections' ? 'text-[#D4AF37] bg-[#1A1A1A]' : 'text-white hover:text-[#D4AF37] hover:bg-[#1A1A1A]'
                  }`}
                >
                  Collections
                  <ChevronDown className={`h-4 w-4 transition-transform ${activeDropdown === 'collections' ? 'rotate-180' : ''}`} />
                </button>
                
                {activeDropdown === 'collections' && (
                  <div className="lg:absolute lg:top-full lg:left-0 lg:mt-1 bg-[#141414] lg:shadow-2xl lg:shadow-[#D4AF37]/10 lg:rounded-2xl lg:border lg:border-[#D4AF37]/20 lg:min-w-[320px] z-50">
                    <div className="p-5">
                      <div className="flex items-center gap-2 mb-4">
                        <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                        <p className="text-xs uppercase tracking-wider text-[#888888] font-body font-semibold">Curated Collections</p>
                      </div>
                      <div className="grid gap-1">
                        {collections.map((col) => {
                          const IconComponent = col.icon;
                          return (
                            <Link
                              key={col.slug}
                              to={`/shop?collection=${col.slug}`}
                              className="flex items-center gap-4 py-3 px-3 rounded-xl hover:bg-[#1A1A1A] transition-all group"
                              onClick={() => { setIsMenuOpen(false); setActiveDropdown(null); }}
                            >
                              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/10 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                <IconComponent className={`h-5 w-5 ${col.color}`} />
                              </div>
                              <div>
                                <span className="text-white group-hover:text-[#D4AF37] font-body font-medium block">{col.name}</span>
                                <span className="text-xs text-[#888888] font-body">{col.description}</span>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </li>

              <li>
                <Link
                  to="/shop?collection=new-arrivals"
                  className="block py-3 px-4 text-base font-semibold text-white hover:text-[#D4AF37] hover:bg-[#1A1A1A] rounded-lg transition-all font-body"
                  onClick={() => { setIsMenuOpen(false); setActiveDropdown(null); }}
                >
                  New Arrivals
                </Link>
              </li>

              <li>
                <Link
                  to="/about"
                  className="block py-3 px-4 text-base font-semibold text-white hover:text-[#D4AF37] hover:bg-[#1A1A1A] rounded-lg transition-all font-body"
                  onClick={() => { setIsMenuOpen(false); setActiveDropdown(null); }}
                >
                  About
                </Link>
              </li>

              <li>
                <Link
                  to="/blog"
                  className="block py-3 px-4 text-base font-semibold text-white hover:text-[#D4AF37] hover:bg-[#1A1A1A] rounded-lg transition-all font-body"
                  onClick={() => { setIsMenuOpen(false); setActiveDropdown(null); }}
                >
                  Blog
                </Link>
              </li>

            </ul>
          </div>
        </nav>
      </header>
    </>
  );
};

export default SerenityHeader;
